<template>
  <Transition>
    <TopBanner :mode="bannerMode" :text="bannerText" v-if="bannerText" />
  </Transition>
  <div class="admin-page employees-page">
    <div class="admin-page-inner employees-page-inner">
      <div class="admin-page-header">
        <div>
          <h1 class="admin-page-title">Mitarbeiterverwaltung</h1>
        </div>
        <div class="admin-action-bar">
          <button
            type="button"
            class="admin-button--primary"
            @click="createNewEmployee()"
          >
            <i class="fa fa-plus mr-2"></i>Neuer Mitarbeiter
          </button>
          <button
            type="button"
            class="admin-button--secondary"
            v-if="canAccessMeldeliste"
            @click="showActionsModal = true"
          >
            <i class="fa fa-cog mr-2"></i>Aktionen
          </button>
          <button
            type="button"
            class="admin-button--secondary"
            @click="showInactiveUsers = !showInactiveUsers; applyFilter()"
          >
            {{ showInactiveUsers ? 'Aktive' : 'Inaktive' }} User anzeigen
          </button>
        </div>
      </div>

      <section class="admin-panel mb-4">
        <div class="admin-panel__body flex flex-col gap-2 sm:flex-row sm:items-center">
          <label class="shrink-0 text-sm font-bold text-gray-700" for="employee-filter">Nach Namen suchen:</label>
          <div class="relative w-full sm:max-w-xs">
            <input
              id="employee-filter"
              v-model="filter"
              class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-800 shadow-sm transition focus:border-gray-500 focus:ring-2 focus:ring-gray-200"
              placeholder="Name oder Personalnummer"
              @input="applyFilter()"
            />
            <button
              v-if="filter"
              type="button"
              class="admin-button--ghost admin-button--icon absolute right-1 top-1/2 -translate-y-1/2 !h-8 !min-h-8 !w-8 !min-w-8"
              aria-label="Suche löschen"
              @click="filter = ''; applyFilter()"
            >
              <i class="fa fa-xmark"></i>
            </button>
          </div>
        </div>
      </section>

      <section v-if="isAdmin && employeeStats.pendingApprovals > 0" class="admin-panel mb-4">
        <div class="admin-panel__body flex flex-col gap-2 sm:flex-row sm:items-center rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3">
          <span class="flex-1 text-sm font-semibold text-amber-800">
            <i class="fa fa-exclamation-triangle mr-2"></i>
            {{ employeeStats.pendingApprovals }} Mitarbeiter warten auf Freigabe durch einen Admin.
          </span>
          <button
            type="button"
            class="admin-button--secondary"
            @click="showPendingApprovals()"
          >
            Anzeigen
          </button>
        </div>
      </section>

      <section class="admin-panel employees-table-panel">
        <div class="admin-panel__header">
          <div>
            <span class="font-bold">
              {{ employeeStats.filtered }} Mitarbeiter
              ({{ employeeStats.filteredActive }} aktiv, {{ employeeStats.filteredInactive }} inaktiv)
            </span>
            <span v-if="employeeStats.filtered !== employeeStats.all" class="ml-2 text-sm font-normal text-gray-500">
              – insgesamt {{ employeeStats.all }}
              ({{ employeeStats.active }} aktiv, {{ employeeStats.inactive }} inaktiv)
            </span>
          </div>
          <div class="flex items-center gap-2 print:hidden">
            <JssColumnManager
              :columns="columnManagerColumns"
              :order="columnOrder"
              :hidden="columnHidden"
              @change="onColumnsChange"
            />
            <button
              type="button"
              class="admin-button--secondary"
              :disabled="!users.length"
              @click="exportView"
            >
              <i class="fa fa-file-excel mr-1"></i>Excel Export
            </button>
          </div>
        </div>
        <div class="mb-2 px-4 text-sm text-gray-500 print:hidden">
          <i class="fa fa-info-circle mr-1"></i>
          Zellen lassen sich direkt bearbeiten. Dokumentwarnungen zeigen bei SUBs fehlende oder abgelaufene Nachweise.
        </div>
        <div ref="spreadsheetContainer" class="jss-container employees-jss-container"></div>
      </section>
    </div>
  </div>
  <UserProfileModal
    v-if="showProfileModal"
    :user="selectedUser"
    @close="showProfileModal = false"
    @updateUser="updateUser"
  />
  <div
    v-if="showActionsModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-[2px]"
    @click.self="showActionsModal = false"
  >
    <div
      class="w-full max-w-lg overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="employee-actions-title"
    >
      <div class="flex items-start justify-between bg-gray-700 px-6 py-5 text-white">
        <div class="flex items-center gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-caspyellow text-gray-800 shadow-sm">
            <i class="fa fa-cog text-lg"></i>
          </div>
          <div>
            <h2 id="employee-actions-title" class="text-xl font-bold leading-tight">
              "Mitarbeiterlisten"
            </h2>
            <p class="mt-1 text-sm text-gray-300">Exporte und Auswertungen erstellen</p>
          </div>
        </div>
        <button
          type="button"
          class="admin-button--ghost admin-button--icon !text-white"
          aria-label="Aktionen schließen"
          @click="showActionsModal = false"
        >
          <i class="fa fa-xmark"></i>
        </button>
      </div>

      <div class="space-y-6 p-6">
        <section>
          <p class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500">Meldeliste</p>
          <button
            type="button"
            class="admin-button--ghost group flex w-full items-center gap-4 rounded-xl px-4 py-4 text-left"
            @click="$router.push({ name: 'AdminMeldeliste' })"
          >
            <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
              <i class="fa fa-table text-lg"></i>
            </span>
            <span class="min-w-0 flex-1">
              <span class="block font-bold">Ordnungsamt-Liste / Meldeliste</span>
              <span class="mt-1 block text-sm font-normal text-gray-500">Bearbeitbare Meldeliste öffnen &amp; exportieren</span>
            </span>
            <i class="fa fa-chevron-right text-sm text-gray-400 transition group-hover:text-blue-600"></i>
          </button>
        </section>

        <section v-if="isAdmin">
          <p class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500">DATEV-Export</p>
          <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <label class="block" for="employee-export-month">
              <span class="mb-2 block text-sm font-bold text-gray-700">Abrechnungsmonat</span>
              <span class="mb-4 block text-xs text-gray-500">Wähle den Monat für die DATEV-Liste aus.</span>
              <input
                id="employee-export-month"
                v-model="selectedMonth"
                type="month"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-normal text-gray-800 shadow-sm transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
            </label>
            <button
              type="button"
              class="admin-button--primary mt-4 flex w-full items-center justify-center gap-2"
              @click="exportDatevList()"
              :disabled="datevExporting"
              :aria-busy="datevExporting"
            >
              <i class="fa fa-file-export"></i>
              {{ datevExporting ? 'Export wird erstellt...' : 'DATEV-Liste erstellen' }}
            </button>
          </div>
        </section>
      </div>

      <div class="flex justify-end border-t border-gray-200 bg-gray-50 px-6 py-4">
        <button
          type="button"
          class="admin-button--ghost"
          @click="showActionsModal = false"
        >
          Abbrechen
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from "../../CASP";
import { useCookies } from "vue3-cookies";
import TopBanner from "@/components/TopBanner";
import UserProfileModal from "./UserProfileModal.vue";
import JssColumnManager from "@/components/JssColumnManager.vue";
import jspreadsheet from "jspreadsheet-ce";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import "jsuites/dist/jsuites.css";
import { clearAdminSession, useAdminSession } from "@/admin/adminSession";
import { applyColumnSettings, installAutocompleteFilters, removeAutocompleteFilters, stickFilterRow } from "@/lib/jssColumns";
import { exportJssView } from "@/lib/jssExport";
import { createRowHighlighter } from "@/lib/jssRowHighlight";
import { installAppendOnlyRowLoading } from "@/lib/jssAppendOnlyRows";
import { sortJssRows } from "@/lib/jssSort";

const COLUMNS = [
  { title: "Aktionen", width: 145, field: "_aktionen", type: "text", readOnly: true, pinned: true },
  { title: "Personalnummer", width: 120, field: "persnr", type: "text", readOnly: true },
  { title: "Vorname", width: 140, field: "name", type: "text" },
  { title: "Nachname", width: 140, field: "surname", type: "text" },
  { title: "Straße", width: 180, field: "street", type: "text" },
  { title: "PLZ", width: 80, field: "zip", type: "text" },
  { title: "Stadt", width: 130, field: "city", type: "text" },
  { title: "Telefonnummer", width: 135, field: "phone", type: "text" },
  { title: "E-Mail", width: 220, field: "email", type: "text" },
  { title: "KFZ-Kennzeichen", width: 135, field: "kennzeichen", type: "text" },
  { title: "Dienstausweisnummer", width: 130, field: "idnr", type: "text" },
  { title: "QR-Text", width: 240, field: "qrValue", type: "text" },
  { title: "Aktiv", width: 70, field: "active", type: "checkbox" },
  { title: "Externer Mitarbeiter", width: 135, field: "is_external", type: "checkbox" },
  { title: "Firmenkürzel", width: 120, field: "Firmenkurzel", type: "text" },
  { title: "Einsatzfähig", width: 105, field: "einsatzfaehig", type: "checkbox", readOnly: true },
  { title: "Einsatzfähig (Manuell)", width: 145, field: "einsatzbereitManuell", type: "checkbox" },
  { title: "Geringfügig Beschäftigt", width: 155, field: "minijob", type: "checkbox" },
  { title: "Eintrittsdatum", width: 115, field: "entryDate", type: "calendar", dateField: true },
  { title: "Austrittsdatum", width: 115, field: "exitDate", type: "calendar", dateField: true },
  { title: "Freigabe ausstehend", width: 135, field: "pendingApproval", type: "checkbox", readOnly: true },
  { title: "Dokumentwarnung", width: 300, field: "_documentWarning", type: "text", readOnly: true }
];

function isoToDisplay(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
}

function displayToIso(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (!match) return String(value);
  let [, day, month, year] = match;
  if (year.length === 2) year = `19${year}`;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function asBoolean(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export default {
  name: "AdminEmployees",
  components: { JssColumnManager, TopBanner, UserProfileModal },
  setup() {
    const { cookies } = useCookies();
    const { stats } = useAdminSession();
    return { cookies, sessionStats: stats };
  },
  data() {
    return {
      token: "",
      users: [],
      tableFilters: {},
      tableFilterTimer: null,
      employeeStats: {
        all: 0,
        active: 0,
        inactive: 0,
        filtered: 0,
        filteredActive: 0,
        filteredInactive: 0,
        nextCspPersnr: "CSP1"
      },
      bannerText: "",
      bannerMode: "success",
      filter: "",
      selectedMonth: new Date().toISOString().slice(0, 7),
      datevExporting: false,
      showActionsModal: false,
      showProfileModal: false,
      selectedUser: null,
      showInactiveUsers: false,
      identityLoadingUserId: null,
      jssInstance: null,
      appendOnlyRowLoadingCleanup: null,
      sortState: null,
      renderedColumns: [],
      viewSettings: {},
      gridClickHandler: null,
      saveTimers: {},
      pendingUserChanges: {},
      rowHighlighter: null
    };
  },
  computed: {
    isSuperadmin() {
      return Boolean(this.sessionStats?.permissions?.superadmin);
    },
    isAdmin() {
      return Boolean(this.sessionStats?.permissions?.admin);
    },
    canAccessMeldeliste() {
      const permissions = this.sessionStats?.permissions || {};
      return this.isAdmin || Boolean(permissions.externalDispo && permissions.editEmployees);
    },
    currentSessionUserId() {
      return this.sessionStats?.user?.id || null;
    },
    viewKey() {
      return "adminemployees";
    },
    allColumns() {
      return COLUMNS.map(column => ({ ...column }));
    },
    columnManagerColumns() {
      return this.allColumns
        .filter(column => !column.pinned)
        .map(column => ({ field: column.field, title: column.title }));
    },
    columnOrder() {
      return this.viewSettings[this.viewKey]?.order || [];
    },
    columnHidden() {
      return this.viewSettings[this.viewKey]?.hidden || [];
    }
  },
  methods: {
    openProfileModal(user) {
      this.selectedUser = user;
      this.showProfileModal = true;
    },
    updateUser(user) {
      this.getUsers(user && user.id);
    },
    impersonateUser(user) {
      if (this.identityLoadingUserId !== null) return;
      if (!confirm(`Identität von ${user.name} ${user.surname} annehmen?`)) return;
      this.identityLoadingUserId = user.id;
      const token = this.cookies.get("caspauth") || this.cookies.get("authtoken");
      if (!token) {
        this.identityLoadingUserId = null;
        this.showBanner("Die Identität konnte nicht angenommen werden.", "error");
        return;
      }
      CASP.impersonateUser(user.id, token, (status, response) => {
        this.identityLoadingUserId = null;
        if (status === 200) {
          clearAdminSession();
          window.location = "/";
          return;
        }
        this.showBanner(response?.message || "Die Identität konnte nicht angenommen werden.", "error");
      });
    },
    sendInvitation(user) {
      CASP.sendInvitation(this.token, user.id, response => {
        if (response) this.showBanner("SMS mit Zugangsdaten gesendet");
        else this.showBanner("SMS konnte nicht gesendet werden.", "error");
      });
    },
    getUsers(highlightUserId = null) {
      CASP.getAdminEmployees({
        // The toggle means "show all" when enabled, not "show only
        // inactive"; omit the active filter in that mode.
        active: this.showInactiveUsers ? undefined : true,
        search: this.filter.trim(),
        columnFilters: this.tableFilters
      }, (status, response) => {
        if (status !== 200 || !response || !Array.isArray(response.users)) {
          this.showBanner("Mitarbeiter konnten nicht geladen werden.", "error");
          return;
        }
        this.users = response.users;
        this.employeeStats = response.stats || this.employeeStats;
        this.renderGrid();
        if (highlightUserId !== null && this.rowHighlighter) {
          this.rowHighlighter.mark(highlightUserId);
        }
      });
    },
    showPendingApprovals() {
      this.showInactiveUsers = true;
      this.tableFilters = { ...this.tableFilters, pendingApproval: "ja" };
      this.applyFilter();
    },
    applyFilter() {
      clearTimeout(this.tableFilterTimer);
      this.tableFilterTimer = setTimeout(() => {
        this.tableFilterTimer = null;
        this.getUsers();
      }, 200);
    },
    updateTableFilters(filters) {
      this.tableFilters = filters;
      this.applyFilter();
    },
    getDocumentWarning(user) {
      if (user.is_external && user.subcontractorDocumentsConfigured === false) {
        return "SUB-Firma nicht zugeordnet";
      }
      if (!user.missingDocuments?.length) return "";
      return `Fehlt/abgelaufen: ${user.missingDocuments.join(", ")}`;
    },
    buildRowData(user, columns) {
      return columns.map(column => this.cellValue(user, column));
    },
    cellValue(user, column) {
      if (column.field === "_documentWarning") return this.getDocumentWarning(user);
      if (column.field === "_aktionen") return "";
      const value = user[column.field];
      if (column.dateField) return isoToDisplay(value);
      if (column.type === "checkbox") return Boolean(value);
      return value === null || value === undefined ? "" : String(value);
    },
    onColumnsChange({ order, hidden }) {
      this.viewSettings = {
        ...this.viewSettings,
        [this.viewKey]: { order, hidden }
      };
      CASP.saveViewSettings(this.viewKey, { order, hidden });
      this.renderGrid();
    },
    renderGrid() {
      const container = this.$refs.spreadsheetContainer;
      if (!container) return;
      removeAutocompleteFilters(container);
      if (this.appendOnlyRowLoadingCleanup) {
        this.appendOnlyRowLoadingCleanup();
        this.appendOnlyRowLoadingCleanup = null;
      }
      if (this.gridClickHandler) {
        container.removeEventListener("click", this.gridClickHandler);
        this.gridClickHandler = null;
      }
      if (container.spreadsheet) {
        try { jspreadsheet.destroy(container); } catch (error) { /* ignore cleanup errors */ }
      }
      container.innerHTML = "";
      this.jssInstance = null;

      const columns = applyColumnSettings(this.allColumns, this.viewSettings[this.viewKey]);
      this.renderedColumns = columns;
      if (this.sortState) {
        const sortColumn = columns.find(column => column.field === this.sortState.field);
        if (sortColumn) {
          this.users = sortJssRows(
            this.users,
            user => this.cellValue(user, sortColumn),
            this.sortState.direction
          );
        }
      }
      const data = this.users.map(user => this.buildRowData(user, columns));
      const vm = this;
      this.jssInstance = jspreadsheet(container, {
        onchange: (instance, cell, colIndex, rowIndex, newValue, oldValue) => {
          vm.handleCellChange(colIndex, rowIndex, newValue, oldValue);
        },
        onbeforechange: (instance, cell, colIndex, rowIndex, newValue) => {
          const column = vm.renderedColumns[colIndex];
          if (!column?.dateField || !newValue) return undefined;
          const displayValue = String(newValue);
          if (!/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(displayValue)) {
            vm.showBanner("Bitte ein gültiges Datum im Format TT.MM.JJJJ eingeben.", "error");
            return false;
          }
          return displayValue;
        },
        oncreatecell: (instance, cell, colIndex, rowIndex, value) => {
          const column = vm.renderedColumns[colIndex];
          if (column?.field === "_documentWarning" && value) {
            cell.classList.add("jss-document-warning");
            cell.title = value;
          }
          if (column?.field === "_aktionen") {
            const user = vm.users[rowIndex];
            if (!user) return;
            let html = '<button type="button" class="admin-button--ghost admin-button--icon jss-employee-action" data-action="edit" title="Profil anzeigen/bearbeiten" aria-label="Profil anzeigen/bearbeiten"><i class="fa fa-pen text-blue-500"></i></button>';
            if (vm.isSuperadmin && vm.currentSessionUserId != user.id) {
              html += '<button type="button" class="admin-button--ghost admin-button--icon jss-employee-action" data-action="impersonate" title="Identität annehmen" aria-label="Identität annehmen"><i class="fa fa-user-secret text-amber-600"></i></button>';
            }
            if (user.phone && vm.isAdmin) {
              html += '<button type="button" class="admin-button--ghost admin-button--icon jss-employee-action" data-action="sms" title="Zugangsdaten per SMS" aria-label="Zugangsdaten per SMS"><i class="fa fa-message text-red-500"></i></button>';
            }
            cell.innerHTML = html;
            cell.classList.add("jss-nowrap");
          }
        },
        onsort: (instance, colIndex, order, newOrder) => {
          const column = vm.renderedColumns[colIndex];
          if (!column) return;
          vm.sortState = {
            field: column.field,
            direction: order === 1 ? "desc" : "asc"
          };
          vm.renderGrid();
        },
        worksheets: [{
          data: data.slice(0, 100),
          columns: columns.map(column => ({
            title: column.title,
            width: column.width,
            type: column.type,
            readOnly: Boolean(column.readOnly),
            ...(column.type === "calendar"
              ? { options: { format: "DD.MM.YYYY", placeholder: "TT.MM.JJJJ" } }
              : {})
          })),
          filters: true,
          tableOverflow: true,
          tableHeight: "calc(100vh - 24rem)",
          tableWidth: "100%",
          columnSorting: true,
          lazyLoading: true,
          defaultColAlign: "left",
          wordWrap: true
        }]
      });
      const worksheet = this.jssInstance[0];
      this.appendOnlyRowLoadingCleanup = installAppendOnlyRowLoading(worksheet, {
        remainingRows: data.slice(100),
        appendRows: rows => rows.forEach(row => worksheet.insertRow(
          row,
          worksheet.rows.length - 1,
          false
        ))
      });
      stickFilterRow(container);
      installAutocompleteFilters(container, this.jssInstance[0], this.renderedColumns, {
        serverSide: true,
        initialFilters: this.tableFilters,
        onFilterChange: filters => this.updateTableFilters(filters)
      });

      this.gridClickHandler = event => {
        const worksheet = vm.jssInstance && vm.jssInstance[0];
        if (!worksheet) return;
        if (event.target.classList.contains("jss_column_filter")) {
          const columnIndex = event.target.getAttribute("data-x");
          if (columnIndex !== null) worksheet.openFilter(parseInt(columnIndex));
          return;
        }
        const button = event.target.closest(".jss-employee-action");
        if (!button) return;
        const row = button.closest("tr[data-y]");
        const user = row && vm.users[parseInt(row.getAttribute("data-y"))];
        if (!user) return;
        const action = button.getAttribute("data-action");
        if (action === "edit") vm.openProfileModal(user);
        if (action === "impersonate") vm.impersonateUser(user);
        if (action === "sms") vm.sendInvitation(user);
      };
      container.addEventListener("click", this.gridClickHandler);
    },
    handleCellChange(columnIndex, rowIndex, newValue, oldValue) {
      if (newValue === oldValue) return;
      const column = this.renderedColumns[columnIndex];
      const user = this.users[rowIndex];
      if (!column || !user || column.readOnly || column.field.startsWith("_")) return;

      let value = newValue;
      if (column.type === "checkbox") value = asBoolean(newValue);
      if (column.dateField) value = displayToIso(newValue);
      user[column.field] = value;
      if (!this.pendingUserChanges[user.id]) this.pendingUserChanges[user.id] = {};
      this.pendingUserChanges[user.id][column.field] = value;
      if (this.saveTimers[user.id]) clearTimeout(this.saveTimers[user.id]);
      this.saveTimers[user.id] = setTimeout(() => {
        delete this.saveTimers[user.id];
        const changes = this.pendingUserChanges[user.id] || {};
        delete this.pendingUserChanges[user.id];
        if (Object.prototype.hasOwnProperty.call(changes, "active")) {
          changes.activeManuallyChanged = true;
        }
        CASP.changeUserWithStatus(user.id, changes, (status, response) => {
          if (status !== 200) {
            this.showBanner(response?.desc || "Mitarbeiter konnte nicht gespeichert werden.", "error");
            this.getUsers();
          } else {
            // Reload the filtered server result so counts, readiness,
            // document warnings, and filter membership stay current.
            this.getUsers(user.id);
          }
        });
      }, 500);
    },
    exportView() {
      if (!this.jssInstance || !this.users.length) return;
      const columns = this.renderedColumns;
      exportJssView({
        filename: `${new Date().toISOString().slice(0, 10)}_mitarbeiter.xlsx`,
        headers: columns.map(column => column.title),
        columnWidths: columns.map(column => column.width),
        rows: this.users.map(user => this.buildRowData(user, columns)),
        worksheet: this.jssInstance[0]
      });
    },
    createNewEmployee() {
      this.selectedUser = {
        id: null,
        name: "",
        surname: "",
        persnr: this.getNextCspPersnr(),
        Ausweisnr: "",
        vacationDaysTakenOutside: 0,
        isNew: true
      };
      this.showProfileModal = true;
    },
    getNextCspPersnr() {
      return this.employeeStats.nextCspPersnr || "CSP1";
    },
    exportOrdnungsamt() {
      this.showActionsModal = false;
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.exportOrdnungsamt((status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200 && response?.otl) {
          window.open(`${process.env.VUE_APP_API_URL}/assets/${response.otl}`, "_blank");
        } else {
          this.showBanner("Fehler beim Erstellen der Ordnungsamt-Liste", "error");
        }
      });
    },
    exportDatevList() {
      if (this.datevExporting) return;
      this.showActionsModal = false;
      this.datevExporting = true;
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.exportDatevForAll(this.selectedMonth, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        this.datevExporting = false;
        if (status === 200 && response?.lodasOtl && response?.readableOtl) {
          const apiUrl = process.env.VUE_APP_API_URL;
          window.open(`${apiUrl}/assets/${response.lodasOtl}`, "_blank");
          window.open(`${apiUrl}/assets/${response.readableOtl}`, "_blank");
          this.showBanner("DATEV-Liste erstellt.");
        } else {
          this.showBanner(response?.desc || "Fehler beim Erstellen der DATEV-Liste", "error");
        }
      });
    },
    showBanner(text, mode = "success") {
      this.bannerText = text;
      this.bannerMode = mode;
      setTimeout(() => { this.bannerText = ""; }, 3000);
    }
  },
  mounted() {
    const spinner = document.getElementById("mainspinner");
    if (spinner) spinner.style.display = "none";
    this.rowHighlighter = createRowHighlighter({
      getContainer: () => this.$refs.spreadsheetContainer,
      getRows: () => this.users
    });
    CASP.getViewSettings((status, response) => {
      if (status === 200 && response) {
        this.viewSettings = response;
        if (this.jssInstance) this.renderGrid();
      }
    });
    this.getUsers();
  },
  beforeUnmount() {
    Object.values(this.saveTimers).forEach(timer => clearTimeout(timer));
    clearTimeout(this.tableFilterTimer);
    if (this.rowHighlighter) this.rowHighlighter.clear();
    if (this.appendOnlyRowLoadingCleanup) {
      this.appendOnlyRowLoadingCleanup();
      this.appendOnlyRowLoadingCleanup = null;
    }
    const container = this.$refs.spreadsheetContainer;
    if (!container) return;
    removeAutocompleteFilters(container);
    if (this.gridClickHandler) container.removeEventListener("click", this.gridClickHandler);
    if (container.spreadsheet) {
      try { jspreadsheet.destroy(container); } catch (error) { /* ignore cleanup errors */ }
    }
  }
};
</script>

<style scoped>
.employees-page {
  display: flex;
  flex-direction: column;
}
.employees-page-inner,
.employees-table-panel {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.employees-page-inner {
  width: 100% !important;
  max-width: none !important;
  margin-right: 0 !important;
  margin-left: 0 !important;
}

.employees-jss-container {
  min-height: 16rem;
  width: 100%;
  overflow: visible;
  text-align: left;
}

.employees-jss-container :deep(.jss_content) {
  display: block;
  overflow: auto;
  width: 100% !important;
}

.employees-jss-container :deep(.jss_container) {
  width: 100% !important;
}

.employees-jss-container :deep(.jss_worksheet) {
  min-width: 100%;
}

.employees-jss-container :deep(.jss_worksheet > thead > tr > td) {
  position: sticky;
  top: 0;
  z-index: 3;
  color: #111827 !important;
  text-align: left;
  background-color: #f3f4f6;
}

.employees-jss-container :deep(.jss_worksheet > thead > tr.jss-filter-row > td) {
  top: var(--jss-header-h, 26px);
}

.employees-jss-container :deep(.jss_worksheet > tbody > tr > td) {
  text-align: left;
}

/* JSS marks the active cell/row with a green background by default. Keep
   selection neutral; successful saves use the temporary green row flash. */
.employees-jss-container :deep(.jss_worksheet .highlight),
.employees-jss-container :deep(.jss_worksheet td.highlight),
.employees-jss-container :deep(.jss_worksheet .highlight-selected),
.employees-jss-container :deep(.jss_worksheet .selection),
.employees-jss-container :deep(.jss_worksheet tr.selected > td) {
  background-color: #e5e7eb !important;
}

.employees-jss-container :deep(.jss_worksheet tr.selected > td:first-child) {
  background-color: #d1d5db !important;
}

.employees-jss-container :deep(.jss_worksheet input[type="checkbox"]) {
  appearance: auto;
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
}

.employees-jss-container :deep(.jss-document-warning) {
  color: #991b1b !important;
  font-weight: 700;
  background: #fee2e2 !important;
}

.employees-jss-container :deep(.jss-employee-action) {
  min-height: 0;
  min-width: 2rem;
  padding: 2px 5px;
  margin: 1px;
  white-space: nowrap;
}

@media (max-width: 1023px) {
  .employees-page {
    min-height: calc(100vh - 3.75rem);
  }
}
</style>
